import { useState } from "react";
import { Check, Globe } from "lucide-react";
import { useMutation } from "@tanstack/react-query";

import {
  LOCALES,
  LOCALE_LABEL,
  localisePath,
  persistLocaleCookie,
  useLocale,
  useTranslation,
  type Locale,
} from "@/i18n";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * Language selector. Persists to a cookie for everyone and to the signed-in
 * member's profile, then navigates to the locale-prefixed URL for the current
 * page so the server renders the chosen language.
 */
export function LanguageSelector({
  variant = "header",
  className,
}: {
  variant?: "header" | "footer";
  className?: string;
}) {
  const locale = useLocale();
  const { t } = useTranslation("navigation");
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const savePreference = useMutation({
    mutationFn: async (next: Locale) => {
      if (!user) return;
      await supabase.from("profiles").update({ preferred_locale: next }).eq("id", user.id);
    },
  });

  const choose = async (next: Locale) => {
    setOpen(false);
    if (next === locale) return;
    persistLocaleCookie(next);
    try {
      await savePreference.mutateAsync(next);
    } catch {
      /* cookie preference still applies */
    }
    const target = localisePath(window.location.pathname, next) + window.location.search;
    window.location.assign(target);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === "header" ? "outline" : "ghost"}
          size="sm"
          aria-label={t("changeLanguage", "Change language")}
          className={cn(
            "gap-2 rounded-xl",
            variant === "header" &&
              "h-10 px-3 border-white/10 bg-white/5 backdrop-blur-xl hover:border-white/20 hover:bg-white/10",
            variant === "footer" && "h-9 px-3 text-foreground/80",
            className,
          )}
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm">{LOCALE_LABEL[locale]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-44 rounded-xl border-white/10 bg-background/90 backdrop-blur-xl"
      >
        {LOCALES.map((l) => (
          <DropdownMenuItem
            key={l}
            onSelect={(e) => {
              e.preventDefault();
              void choose(l);
            }}
            aria-current={l === locale ? "true" : undefined}
            className="flex items-center justify-between gap-2"
          >
            <span lang={l}>{LOCALE_LABEL[l]}</span>
            {l === locale && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
